import { createFileRoute, Link } from "@tanstack/react-router";
import { JsonLd } from "~/components/seo/JsonLd";
import {
  CHANCENKARTE_RESUME_LANDING_PATH,
} from "~/lib/seo/public-landing-routes";
import { breadcrumbSchema, schemaGraph, webPageSchema } from "~/lib/seo/schema";

export const CHANCENKARTE_RESUME_URL =
  `https://alchema.eu${CHANCENKARTE_RESUME_LANDING_PATH}` as const;
export const CHANCENKARTE_RESUME_TITLE =
  "Chancenkarte Resume Builder Guide for Germany | Alchema";
export const CHANCENKARTE_RESUME_DESCRIPTION =
  "Prepare a clear, evidence-based resume for a German Chancenkarte job search, then review every AI-assisted suggestion before export.";

export const Route = createFileRoute("/en/chancenkarte-resume-builder")({
  head: () => ({
    meta: [
      { title: CHANCENKARTE_RESUME_TITLE },
      { name: "description", content: CHANCENKARTE_RESUME_DESCRIPTION },
      { property: "og:title", content: CHANCENKARTE_RESUME_TITLE },
      { property: "og:description", content: CHANCENKARTE_RESUME_DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: CHANCENKARTE_RESUME_URL },
      { property: "og:locale", content: "en_EU" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "index, follow" },
    ],
    links: [{ rel: "canonical", href: CHANCENKARTE_RESUME_URL }],
  }),
  component: ChancenkarteResumeBuilderPage,
});

function ChancenkarteResumeBuilderPage() {
  const jsonLd = schemaGraph(
    webPageSchema({
      name: CHANCENKARTE_RESUME_TITLE,
      description: CHANCENKARTE_RESUME_DESCRIPTION,
      url: CHANCENKARTE_RESUME_URL,
    }),
    breadcrumbSchema([
      { name: "Home", url: "/" },
      {
        name: "Chancenkarte resume guide",
        url: CHANCENKARTE_RESUME_LANDING_PATH,
      },
    ]),
  );

  return (
    <div style={{ backgroundColor: "var(--bg)", color: "var(--text)" }}>
      <JsonLd json={jsonLd} />
      <main id="main-content">
        <section className="border-b" style={{ borderColor: "var(--border)" }}>
          <div className="mx-auto grid max-w-6xl gap-12 px-5 pb-20 pt-16 sm:px-8 sm:pt-24 lg:grid-cols-[minmax(0,1.2fr)_minmax(19rem,0.8fr)] lg:items-end">
            <div>
              <p
                className="text-sm font-semibold uppercase tracking-[0.18em]"
                style={{ color: "var(--accent-text)" }}
              >
                German Opportunity Card job search
              </p>
              <h1 className="mt-5 max-w-4xl text-4xl font-bold tracking-[-0.04em] sm:text-6xl">
                Build a resume for your Chancenkarte job search
              </h1>
              <p
                className="mt-7 max-w-3xl text-lg leading-8 sm:text-xl"
                style={{ color: "var(--text-secondary)" }}
              >
                Turn your own work history, qualifications, and skills into a clear resume for
                employers in Germany. Alchema can help structure and tailor the document; you
                review and approve every AI-assisted suggestion before using it.
              </p>
              <div className="mt-9 flex flex-wrap items-center gap-4">
                <Link
                  to="/register"
                  className="inline-flex min-h-11 items-center rounded-xl px-6 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4"
                  style={{ backgroundColor: "var(--accent)" }}
                >
                  Start your resume
                </Link>
                <Link
                  to="/blog/$slug"
                  params={{ slug: "germany-blue-card-chancenkarte-2026" }}
                  className="inline-flex min-h-11 items-center px-1 py-3 text-sm font-semibold underline decoration-1 underline-offset-4"
                >
                  Read the current Chancenkarte guide
                </Link>
              </div>
            </div>

            <aside
              aria-labelledby="scope-heading"
              className="border-y py-7 lg:border-y-0 lg:border-l lg:py-2 lg:pl-10"
              style={{ borderColor: "var(--border)" }}
            >
              <h2 id="scope-heading" className="text-lg font-semibold">
                What this page does — and does not do
              </h2>
              <p className="mt-4 leading-7" style={{ color: "var(--text-secondary)" }}>
                It helps you prepare application documents from facts you provide. It does not
                assess visa eligibility, award Chancenkarte points, provide legal advice, or
                predict an embassy or employer decision.
              </p>
            </aside>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-5 py-20 sm:px-8">
          <div className="grid gap-12 lg:grid-cols-[0.72fr_1.28fr]">
            <div>
              <p
                className="text-sm font-semibold uppercase tracking-[0.18em]"
                style={{ color: "var(--accent-text)" }}
              >
                A practical document workflow
              </p>
              <h2 className="mt-4 text-3xl font-semibold tracking-tight">
                Keep the evidence yours
              </h2>
            </div>
            <ol className="divide-y" style={{ borderColor: "var(--border)" }}>
              {[
                {
                  title: "Start with your real history",
                  text: "Add your actual roles, dates, education, languages, qualifications, and projects. Missing facts should stay missing until you can verify them.",
                },
                {
                  title: "Adapt for the role",
                  text: "Compare the resume with a job description and decide which supported experience deserves clearer wording or more context.",
                },
                {
                  title: "Check the German reading flow",
                  text: "Use a simple, reverse-chronological structure and review whether headings, dates, and contact details are easy for people and common parsers to follow.",
                },
                {
                  title: "Review before export",
                  text: "Confirm every material statement yourself. AI-assisted wording is a draft, not evidence and not an application outcome.",
                },
              ].map((step, index) => (
                <li key={step.title} className="grid gap-3 py-7 first:pt-0 sm:grid-cols-[3rem_1fr]">
                  <span className="text-sm font-semibold" style={{ color: "var(--accent-text)" }}>
                    0{index + 1}
                  </span>
                  <div>
                    <h3 className="text-lg font-semibold">{step.title}</h3>
                    <p className="mt-2 leading-7" style={{ color: "var(--text-secondary)" }}>
                      {step.text}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>
      </main>
    </div>
  );
}
